import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  alpha
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserNotificationTrigger } from '../../types';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';
import { forwardRef } from 'react';

interface ConfirmDeleteModalProps {
  open: boolean;
  trigger: UserNotificationTrigger | null;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const Transition = forwardRef<
  unknown,
  TransitionProps & {
    children: React.ReactElement;
  }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const ConfirmDeleteModal = ({
  open,
  trigger,
  onConfirm,
  onCancel,
  isLoading = false
}: ConfirmDeleteModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!trigger) return null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: theme.shadows[8]
        }
      }}
      sx={{
        zIndex: theme.zIndex.modal + 1 // Ensure it appears above other modals
      }}
    >
      {isMobile ? (
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onCancel}
              aria-label="close"
              disabled={isLoading}
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {t('userNotificationTriggers.confirmDelete.title')}
            </Typography>
          </Toolbar>
        </AppBar>
      ) : (
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmberIcon color="warning" />
            <Typography variant="h6" component="span">
              {t('userNotificationTriggers.confirmDelete.title')}
            </Typography>
          </Box>
        </DialogTitle>
      )}

      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('userNotificationTriggers.confirmDelete.message')}
        </Typography>

        <Box
          sx={{
            backgroundColor: theme.palette.paper.light,
            border: '1px solid',
            borderColor: theme.palette.alphas.divider,
            borderRadius: 1,
            p: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2,  }}>
            {t('userNotificationTriggers.confirmDelete.triggerDetails')}
          </Typography>

          <Box display="flex" flexDirection="column" gap={1.5}>
            {trigger.clientName && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('userNotificationTriggers.fields.client')}
                </Typography>
                <Typography variant="body2" >
                  {trigger.clientName}
                </Typography>
              </Box>
            )}

            {trigger.agentName && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('userNotificationTriggers.fields.agent')}
                </Typography>
                <Typography variant="body2" >
                  {trigger.agentName}
                </Typography>
              </Box>
            )}

            {trigger.workflowName && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('userNotificationTriggers.fields.workflow')}
                </Typography>
                <Typography variant="body2" >
                  {trigger.workflowName}
                </Typography>
              </Box>
            )}

            {trigger.stepName && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('userNotificationTriggers.fields.step')}
                </Typography>
                <Typography variant="body2" >
                  {trigger.stepName}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {t('userNotificationTriggers.fields.status')}
              </Typography>
              <Chip
                label={trigger.status ? t('common.active') : t('common.inactive')}
                color={trigger.status ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
            border: '1px solid',
            borderColor: alpha(theme.palette.warning.main, 0.3),
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
            {t('userNotificationTriggers.confirmDelete.warning')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          color="inherit"
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? t('common.deleting') : t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
